import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { List, X, Cross, CaretDown } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_9d663873-ece1-460a-abc5-f98dcd211b2a/artifacts/qilm7q3e_A087EB80-08A6-4F5B-B90E-AA189E10D04B.png";

const navItems = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Leadership', path: '/leadership' },
  { label: 'Structure', path: '/structure' },
  { label: 'Membership', path: '/membership' },
  { label: 'News', path: '/news' },
  { label: 'Events', path: '/events' },
  { label: 'Gallery', path: '/gallery' },
  { label: 'Documents', path: '/documents' },
  { label: 'Contact', path: '/contact' },
  { label: 'Support', path: '/support' },
];

export const PublicHeader = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 bg-[#F5F2EB]/95 backdrop-blur-md border-b border-[#D8D2C4]" data-testid="public-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
            <img src={LOGO_URL} alt="Grand Priory Crest" className="h-14 w-14 object-contain" />
            <div className="hidden sm:block">
              <p className="font-heading text-lg font-semibold text-[#1A1614] leading-tight">Grand Priory</p>
              <p className="text-xs text-[#4A453E]">of the Caribbean</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            {navItems.slice(0, 8).map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link text-sm font-medium transition-colors ${
                  location.pathname === item.path 
                    ? 'text-[#7B1818]' 
                    : 'text-[#4A453E] hover:text-[#7B1818]'
                }`}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
              >
                {item.label}
              </Link>
            ))}
            {/* More dropdown */}
            <div className="relative group">
              <button className="flex items-center gap-1 text-sm font-medium text-[#4A453E] hover:text-[#7B1818]">
                More <CaretDown size={14} />
              </button>
              <div className="absolute right-0 mt-2 w-40 bg-white border border-[#D8D2C4] shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                {navItems.slice(8).map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="block px-4 py-2 text-sm text-[#4A453E] hover:bg-[#F5F2EB] hover:text-[#7B1818]"
                    data-testid={`nav-link-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </nav>

          {/* Admin Link & Mobile Menu Button */}
          <div className="flex items-center gap-4">
            <Link
              to="/login"
              className="hidden sm:block text-sm font-medium text-[#7B1818] hover:text-[#5C1212]"
              data-testid="admin-portal-link"
            >
              Membership Portal
            </Link>
            <button
              className="lg:hidden p-2 text-[#1A1614]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-[#D8D2C4]" data-testid="mobile-nav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`block py-2 text-sm font-medium ${
                  location.pathname === item.path 
                    ? 'text-[#7B1818]' 
                    : 'text-[#4A453E]'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/login"
              className="block py-2 text-sm font-medium text-[#7B1818]"
              onClick={() => setMobileMenuOpen(false)}
            >
              Membership Portal
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
};

export const PublicFooter = () => {
  return (
    <footer className="bg-[#1A1614] text-[#F5F2EB] py-16" data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Logo & Description */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-4 mb-6">
              <img src={LOGO_URL} alt="Grand Priory Crest" className="h-16 w-16 object-contain" />
              <div>
                <p className="font-heading text-xl font-semibold">Grand Priory of the Caribbean</p>
                <p className="text-sm text-[#8A847A]">SCMOTH 1804 OSMTJ</p>
              </div>
            </div>
            <p className="text-sm text-[#8A847A] leading-relaxed max-w-md">
              A Christian chivalric and charitable Order dedicated to heritage, faith, leadership, 
              service, and discipline throughout the Caribbean region.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-overline mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {['About', 'Leadership', 'Membership', 'Contact'].map((item) => (
                <li key={item}>
                  <Link 
                    to={`/${item.toLowerCase()}`}
                    className="text-sm text-[#8A847A] hover:text-[#C5A059] transition-colors"
                  >
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-overline mb-4">Contact</h4>
            <ul className="space-y-2 text-sm text-[#8A847A]">
              <li>Grand Priory Chancery</li>
              <li>Caribbean Region</li>
              <li className="hover:text-[#C5A059] transition-colors cursor-pointer">
                info@scmoth1804caribbean.org
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-[#362E29] flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs text-[#8A847A]">
            © {new Date().getFullYear()} Grand Priory of the Caribbean SCMOTH 1804 OSMTJ. All rights reserved.
          </p>
          <div className="flex items-center gap-2 text-[#C5A059]">
            <Cross size={16} weight="fill" />
            <span className="text-xs font-medium tracking-wide">NON NOBIS DOMINE</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const PublicLayout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col bg-[#F5F2EB]">
      <PublicHeader />
      <main className="flex-1">
        {children}
      </main>
      <PublicFooter />
    </div>
  );
};

export default PublicLayout;
